/**
 * Connection status indicator with latency and reconnection display
 */

import { useGameStore } from '../../stores/gameStore';
import clsx from 'clsx';

interface ConnectionStatusProps {
  connected: boolean;
}

export function ConnectionStatus({ connected }: ConnectionStatusProps) {
  const latency = useGameStore((s) => s.latency);
  const reconnecting = useGameStore((s) => s.reconnecting);

  const getStatusIndicator = () => {
    if (reconnecting) return 'bg-yellow-500 animate-pulse';
    if (!connected) return 'bg-red-500';
    if (latency < 50) return 'bg-green-500';
    if (latency < 150) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getStatusText = () => {
    if (reconnecting) {
      return (
        <span className="text-yellow-600">
          <span className="hidden sm:inline">Reconnecting...</span>
          <span className="sm:hidden">...</span>
        </span>
      );
    }
    if (!connected) {
      return (
        <span className="text-red-600">
          <span className="hidden sm:inline">Disconnected</span>
          <span className="sm:hidden">Offline</span>
        </span>
      );
    }
    return (
      <span className="text-slate-600 dark:text-slate-400">
        {latency > 0 ? `${latency}ms` : 'Connected'}
      </span>
    );
  };

  return (
    <div
      className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm whitespace-nowrap"
      title={reconnecting ? 'Reconnecting' : connected ? `Connected${latency > 0 ? ` (${latency}ms)` : ''}` : 'Disconnected'}
    >
      <span
        className={clsx('w-2 h-2 rounded-full', getStatusIndicator())}
        aria-hidden="true"
      />
      {getStatusText()}
    </div>
  );
}
